import { useState, useEffect, useRef } from 'react';
import { apiCall } from '../services/api';
import { supabase } from '../lib/supabase';
import { useApp } from './useApp';

export function useActions(domain = null, timestamp = null, jobId = null) {
  const { jobsRevision } = useApp();
  const [actions, setActions] = useState([]);
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [stats, setStats] = useState({
    totalActions: 0,
    completedActions: 0,
    inProgressActions: 0,
    estimatedTimeRemaining: 0,
    highImpactCompleted: 0,
    completionPercentage: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [freemiumData, setFreemiumData] = useState(null);
  const subscriptionRef = useRef(null);

  const fetchActions = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      
      // Build query params
      const params = new URLSearchParams();
      if (domain) params.append('domain', domain);
      if (timestamp) params.append('timestamp', timestamp);
      if (jobId) params.append('jobId', jobId);
      
      const response = await apiCall(`/actions${params.toString() ? '?' + params.toString() : ''}`, {
        method: 'GET'
      });
      
      if (response.success) {
        setActions(response.actions || []);
        // Set executive summary if available
        if (response.executiveSummary) {
          setExecutiveSummary(response.executiveSummary);
        }
        
        // Set freemium data if available
        if (response.isFreemiumPreview) {
          setFreemiumData({
            isFreemiumPreview: true,
            totalActionsAvailable: response.totalActionsAvailable,
            actionsShown: response.actionsShown
          });
        } else {
          setFreemiumData(null);
        }
      } else {
        throw new Error(response.error || 'Failed to fetch actions');
      }
      
      // Fetch stats
      const statsResponse = await apiCall(`/actions/stats${params.toString() ? '?' + params.toString() : ''}`, {
        method: 'GET'
      });
      
      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }
      
    } catch (err) {
      console.error('Error fetching actions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateActionStatus = async (actionId, newStatus) => {
    try {
      // Clear any previous errors
      setError(null);
      
      // Check if we have auth token
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        throw new Error('You must be logged in to update actions');
      }
      
      const response = await apiCall(`/actions/${actionId}/progress`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.success) {
        // Update local state
        setActions(prev => prev.map(action => 
          action.id === actionId 
            ? { ...action, status: newStatus }
            : action
        ));
        
        // Refresh without replacing the open report with the loading screen.
        await fetchActions({ silent: true });
        return true;
      }
      throw new Error(response.error || 'Failed to update action');
      
    } catch (err) {
      console.error('Error updating action:', err);
      setError(err.message);
      // Show alert to user
      alert(`Error: ${err.message}`);
      return false;
    }
  };

  // Set up real-time subscription
  const setupRealtimeSubscription = async () => {
    try {
      // Get subscription config from API
      const response = await apiCall('/actions/subscribe', {
        method: 'POST'
      });
      
      if (response.success) {
        // Subscribe to user's action updates
        const channel = supabase
          .channel(response.channel)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: response.table,
              filter: response.filter
            },
            (payload) => {
              console.log('Real-time update received:', payload);
              
              // Handle different event types
              if (payload.eventType === 'UPDATE') {
                // Update local state for the specific action
                setActions(prev => prev.map(action => 
                  action.id === payload.new.action_id 
                    ? { 
                        ...action, 
                        status: payload.new.status,
                        started_at: payload.new.started_at,
                        completed_at: payload.new.completed_at,
                        notes: payload.new.notes,
                        effectiveness_rating: payload.new.effectiveness_rating
                      }
                    : action
                ));
                
                // Mark update received
                setLastUpdate(new Date());
                
                // Refresh stats without hiding the open report.
                fetchActions({ silent: true });
              }
            }
          )
          .subscribe((status) => {
            setIsConnected(status === 'SUBSCRIBED');
          });
        
        subscriptionRef.current = channel;
      }
    } catch (err) {
      console.error('Error setting up real-time subscription:', err);
    }
  };

  useEffect(() => {
    // Clean up any existing subscription first
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
      setIsConnected(false);
    }
    
    fetchActions();
    setupRealtimeSubscription();
    
    // Cleanup subscription on unmount
    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
        setIsConnected(false);
      }
    };
  }, [domain, timestamp, jobId]);

  useEffect(() => {
    if (jobsRevision > 0) {
      fetchActions({ silent: true });
    }
  }, [jobsRevision]);

  return {
    actions,
    executiveSummary,
    stats,
    loading,
    error,
    refetch: fetchActions,
    updateActionStatus,
    isConnected,
    lastUpdate,
    freemiumData
  };
}